import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, ChevronRight, ShieldAlert, Inbox, ListChecks } from 'lucide-react';

const KIND_META = {
  breach_agencia: { icon: ShieldAlert, label: 'Brecha · 72h Agencia' },
  breach_task:    { icon: ListChecks,  label: 'Tarea legal de brecha' },
  arco_request:   { icon: Inbox,       label: 'Solicitud ARCO+' },
};

const SEVERITY_TONE = {
  critica: { color: '#7F1D1D', bg: '#FEE2E2', border: '#FCA5A5' },
  alta:    { color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' },
  media:   { color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
};

function formatHours(h) {
  if (h < 0) return `Vencido hace ${Math.abs(h)}h`;
  if (h === 0) return 'Vence ahora';
  if (h < 24) return `${h}h restantes`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h restantes`;
}

export default function SlaAlertsWidget({ alerts }) {
  const navigate = useNavigate();
  if (!alerts?.length) return null;

  const critical = alerts.filter((a) => a.severity === 'critica').length;
  const high = alerts.filter((a) => a.severity === 'alta').length;
  const medium = alerts.filter((a) => a.severity === 'media').length;
  // Tono según severidad dominante
  const dominantColor = critical > 0 ? '#DC2626' : high > 0 ? '#EF4444' : '#F59E0B';
  const dominantBg    = critical > 0 ? '#FEE2E2' : high > 0 ? '#FEF2F2' : '#FFFBEB';
  const dominantBorder = critical > 0 ? '#DC2626' : high > 0 ? '#F87171' : '#FBBF24';
  const dominantText  = critical > 0 ? '#7F1D1D' : high > 0 ? '#991B1B' : '#92400E';

  return (
    <section data-testid="sla-alerts-widget" className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
      {/* Caja de impacto · número grande */}
      <div
        className="border-2 flex flex-col justify-center px-5 py-5"
        style={{ backgroundColor: dominantBg, borderColor: dominantBorder }}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <AlertTriangle size={12} style={{ color: dominantColor }} />
          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: dominantText }}>
            SLA en riesgo
          </span>
        </div>
        <div className="text-[56px] font-bold leading-none tracking-tight" style={{ color: dominantColor }}>
          {alerts.length}
        </div>
        <div className="text-[11px] mt-1.5" style={{ color: dominantText }}>
          plazo{alerts.length === 1 ? '' : 's'} legal{alerts.length === 1 ? '' : 'es'} por vencer
        </div>
        {/* Desglose por severidad */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-3 border-t" style={{ borderColor: dominantBorder + '55' }}>
          {critical > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: '#7F1D1D' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />
              {critical} vencido{critical === 1 ? '' : 's'}
            </span>
          )}
          {high > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: '#B91C1C' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" />
              {high} próximo{high === 1 ? '' : 's'}
            </span>
          )}
          {medium > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: '#B45309' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
              {medium} atento
            </span>
          )}
        </div>
      </div>

      {/* Lista de alertas */}
      <div className="border-2 border-l-0 border-[#FECACA] bg-white divide-y divide-[#F3F4F6]">
        {alerts.map((a) => {
          const kind = KIND_META[a.kind] || { icon: AlertTriangle, label: a.kind };
          const tone = SEVERITY_TONE[a.severity] || SEVERITY_TONE.media;
          const Icon = kind.icon;
          return (
            <button
              key={`${a.kind}-${a.id}`}
              onClick={() => navigate(a.action_url)}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#FAFBFC] transition"
              data-testid={`sla-alert-${a.kind}-${a.id.slice(0, 8)}`}
            >
              <div
                className="w-7 h-7 flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: tone.bg, border: `1px solid ${tone.border}` }}
              >
                <Icon size={13} style={{ color: tone.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#1C2329] truncate">{a.title}</div>
                <div className="text-[11px] text-[#6B7280] truncate">{a.meta} · {kind.label}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ color: tone.color }}>
                  <Clock size={10} />
                  {formatHours(a.hours_left)}
                </div>
              </div>
              <ChevronRight size={13} className="text-[#9CA3AF] flex-shrink-0" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
